import React from 'react';
import CodeBlock from '../components/CodeBlock';

const ApiKeys = () => {
  return (
    <div className="page">
      <div className="page-header">
        <h1>API Keys</h1>
        <p>
          Gérez vos clés API pour l'authentification publique.
        </p>
      </div>

      <div className="section">
        <h2>GET /api/api-keys</h2>
        <p>
          Liste toutes les API Keys de l'utilisateur connecté.
        </p>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-get">GET</span>
            <span className="endpoint-path">/api/api-keys</span>
          </div>
          <div className="endpoint-description">
            Récupère toutes les API Keys de l'utilisateur
          </div>

          <div className="alert alert-warning" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            ⚠️ Ces endpoints nécessitent un JWT obtenu via <code>/auth</code> (email/password), pas via API Key,
            ainsi que le header <code>X-Workspace-Id</code>.
          </div>

          <h4>Headers requis:</h4>
          <table className="params-table">
            <thead>
              <tr>
                <th>Header</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Authorization</code></td>
                <td>Bearer JWT_TOKEN (obtenu via /auth classique)</td>
              </tr>
              <tr>
                <td><code>X-Workspace-Id</code></td>
                <td>ID du workspace concerné</td>
              </tr>
              <tr>
                <td><code>Content-Type</code></td>
                <td>application/json</td>
              </tr>
            </tbody>
          </table>

          <h4>Exemple de requête:</h4>
          <CodeBlock
            code={`curl -X GET http://127.0.0.1:8000/api/api-keys \\
  -H "Authorization: Bearer VOTRE_JWT_TOKEN" \\
  -H "Content-Type: application/json"`}
            language="curl"
            showCopy={true}
          />

          <h4>Réponse:</h4>
          <CodeBlock
            code={`{
  "api_keys": [
    {
      "id": 1,
      "name": "Ma première API Key",
      "apiKey": "whk_live_xxxx...xxxx",
      "isActive": true,
      "createdAt": "2024-01-15T10:30:00Z",
      "lastUsedAt": "2024-01-20T14:00:00Z",
      "expiresAt": null
    },
    {
      "id": 2,
      "name": "API Key de production",
      "apiKey": "whk_live_yyyy...yyyy",
      "isActive": true,
      "createdAt": "2024-01-16T09:00:00Z",
      "lastUsedAt": null,
      "expiresAt": "2025-01-16T09:00:00Z"
    }
  ]
}`}
            language="json"
            showCopy={true}
          />
        </div>
      </div>

      <div className="section">
        <h2>POST /api/api-keys</h2>
        <p>Crée une nouvelle API Key dans le workspace courant.</p>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-post">POST</span>
            <span className="endpoint-path">/api/api-keys</span>
          </div>

          <h4>Body</h4>
          <CodeBlock language="json" code={`{
  "name": "Ma première API Key",
  "expiresAt": "2025-01-16T09:00:00Z"
}`} />
          <p>
            <code>name</code> optionnel (défaut « Nouvelle API Key »), <code>expiresAt</code> optionnel
            (par défaut : pas d'expiration).
          </p>

          <h4>Réponse 201</h4>
          <CodeBlock language="json" code={`{
  "message": "API Key créée avec succès",
  "api_key": {
    "id": 3,
    "name": "Ma première API Key",
    "apiKey": "whk_live_xxxxxxxxxxxxxxxxxxxxxxxx",
    "key": "whk_live_xxxxxxxxxxxxxxxxxxxxxxxx",
    "createdAt": "2024-01-15T10:30:00+00:00",
    "expiresAt": "2025-01-16T09:00:00+00:00"
  }
}`} />
          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            ⚠️ La clé complète (<code>apiKey</code>) n'est retournée <strong>qu'à la création</strong>.
            Lors d'un <code>GET</code>, elle est masquée (<code>whk_live_xxxx...xxxx</code>). Conservez-la précieusement.
          </div>
        </div>
      </div>

      <div className="section">
        <h2>PUT /api/api-keys/{'{id}'}</h2>
        <p>Active/désactive une clé ou la renomme.</p>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-put">PUT</span>
            <span className="endpoint-path">/api/api-keys/{'{id}'}</span>
          </div>
          <h4>Body</h4>
          <CodeBlock language="json" code={`{
  "isActive": false,
  "name": "Nouveau nom"
}`} />
        </div>
      </div>

      <div className="section">
        <h2>DELETE /api/api-keys/{'{id}'}</h2>
        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-delete">DELETE</span>
            <span className="endpoint-path">/api/api-keys/{'{id}'}</span>
          </div>
          <p>Supprime définitivement l'API Key.</p>
        </div>
      </div>
    </div>
  );
};

export default ApiKeys;
